import { convertAmount, DEFAULT_CURRENCY } from '@/helpers/currency'

/**
 * Base price fields only (no *Local*). Optional `currencyContext` supplies `currency` / `currencySymbol`
 * when `item` has an amount but omits them (e.g. partner row under a flight card).
 */
export function sourceAmountAndCurrency (item, currencyContext = null) {
  if (item == null) {
    return { amount: 0, currency: DEFAULT_CURRENCY }
  }
  const ctx = currencyContext || {}
  if (item.minPrice != null) {
    const c = item.currency || ctx.currency
    if (c) {
      return {
        amount: Number(item.minPrice),
        currency: String(c).toUpperCase()
      }
    }
  }
  if (item.price != null) {
    const c = item.currency || ctx.currency
    if (c) {
      return {
        amount: Number(item.price),
        currency: String(c).toUpperCase()
      }
    }
  }
  return { amount: 0, currency: DEFAULT_CURRENCY }
}

/** Symbol for the quoted price before conversion (partner/API), not from *Local* fields */
export function originalPriceSymbol (item, currencyContext = null) {
  const sym = item && (item.currencySymbol || item.currency)
  if (sym) return String(sym)
  if (currencyContext) {
    const c = currencyContext.currencySymbol || currencyContext.currency
    if (c) return String(c)
  }
  return ''
}

export function destinationDisplaySymbol (state) {
  const code = state.destinationCurrency
  return state.currenciesByCode[code]?.symbol ?? code ?? ''
}

/**
 * @returns {{ amount: number, symbol: string }}
 */
export function displayAmountSymbol (rawAmount, sourceCurrencyIso, originalSymbol, state) {
  if (!state.currenciesLoaded) {
    return {
      amount: rawAmount,
      symbol: originalSymbol || sourceCurrencyIso || ''
    }
  }
  const converted = convertAmount(
    rawAmount,
    sourceCurrencyIso,
    state.destinationCurrency,
    state.currenciesByCode
  )
  return {
    amount: converted,
    symbol: destinationDisplaySymbol(state)
  }
}

/**
 * @returns {{ amount: number, symbol: string }}
 */
export function itemDisplayAmountAndSymbol (item, state, currencyContext = null) {
  const { amount, currency } = sourceAmountAndCurrency(item, currencyContext)
  const sym = originalPriceSymbol(item, currencyContext)
  return displayAmountSymbol(amount, currency, sym, state)
}

function partnerLineRaw (item, state, currencyContext) {
  const { amount, currency } = sourceAmountAndCurrency(item, currencyContext)
  let raw = amount
  if (
    state.siteType === 'hotels' &&
    state.hotelPriceDisplayMode === 'nightly'
  ) {
    raw = amount / Math.max(1, state.hotelStayNights || 1)
  }
  const sym = originalPriceSymbol(item, currencyContext)
  return { raw, currency, sym }
}

/**
 * Partner / room row (hotels: respects nightly mode)
 * @returns {{ amount: number, symbol: string }}
 */
export function partnerLineDisplay (item, state, currencyContext = null) {
  const { raw, currency, sym } = partnerLineRaw(item, state, currencyContext)
  return displayAmountSymbol(raw, currency, sym, state)
}

/** Numeric value for sliders / filter comparison (raw until rates load) */
export function itemValueForFilter (item, state) {
  const { amount, currency } = sourceAmountAndCurrency(item)
  if (!state.currenciesLoaded) {
    return amount
  }
  return convertAmount(
    amount,
    currency,
    state.destinationCurrency,
    state.currenciesByCode
  )
}

/** Hotel card: stay total or nightly line — numeric for filters */
export function hotelComparableValueForFilter (result, state) {
  const nights = Math.max(1, state.hotelStayNights || 1)
  const { amount, currency } = sourceAmountAndCurrency(result)
  const raw =
    state.hotelPriceDisplayMode === 'nightly' ? amount / nights : amount
  if (!state.currenciesLoaded) {
    return raw
  }
  return convertAmount(
    raw,
    currency,
    state.destinationCurrency,
    state.currenciesByCode
  )
}

/** @returns {{ amount: number, symbol: string }} */
export function hotelComparableDisplay (result, state) {
  const nights = Math.max(1, state.hotelStayNights || 1)
  const { amount, currency } = sourceAmountAndCurrency(result)
  const raw =
    state.hotelPriceDisplayMode === 'nightly' ? amount / nights : amount
  const sym = originalPriceSymbol(result)
  return displayAmountSymbol(raw, currency, sym, state)
}
